import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '@/app/store'
import { apiService } from '@/services/api'
import { buildEndpoint, isCustomerServiceUser } from '@/utils/apiUtils'
import {
  emptyPromotionEvaluation,
  promotionEvaluationFromResponse,
  type PromotionEvaluation,
} from './promotionEvaluation'

interface OffersState {
  evaluation: PromotionEvaluation
  isLoading: boolean
  lastEvalKey: string | null
  requestId: number
}

const initialState: OffersState = {
  evaluation: emptyPromotionEvaluation,
  isLoading: false,
  lastEvalKey: null,
  requestId: 0,
}

function fingerprintFromCartInfo(
  cartInfo: Record<string, unknown> | null | undefined,
  cartItems: Array<Record<string, unknown>>,
): string {
  if (!cartInfo) return ''
  const parts = [
    String(cartInfo.id ?? ''),
    String(cartInfo.total_price ?? ''),
    String(cartInfo.price ?? cartInfo.sub_total ?? ''),
  ]
  for (const item of cartItems) {
    parts.push(`${item.id ?? ''}:${item.quantity ?? ''}`)
  }
  return parts.join('|')
}

function resolveCartContext(state: RootState): {
  cartId: number | null
  fingerprint: string
} {
  const isCs = isCustomerServiceUser()
  if (isCs) {
    const cartInfo = state.csCart.cartInfo
    const cartItems = state.csCart.cartItems ?? []
    const rawId = cartInfo?.id
    const cartId = rawId != null && String(rawId).length > 0 ? Number(rawId) : null
    return {
      cartId: cartId != null && !Number.isNaN(cartId) && cartId > 0 ? cartId : null,
      fingerprint: fingerprintFromCartInfo(cartInfo, cartItems),
    }
  }

  const cartInfo = state.cartItems.cartInfo
  const cartItems = state.cartItems.cartItems ?? []
  const rawId = cartInfo?.id
  const cartId = rawId != null && String(rawId).length > 0 ? Number(rawId) : null
  return {
    cartId: cartId != null && !Number.isNaN(cartId) && cartId > 0 ? cartId : null,
    fingerprint: fingerprintFromCartInfo(cartInfo, cartItems),
  }
}

/**
 * Evaluates the current cart offer via platform promotions endpoint.
 * Skips duplicate cart/payment snapshots. Never throws — failures clear the offer.
 * Never calls cart reload APIs — only POST /cashier|customerService/promotions/evaluate.
 */
export const evaluateCartOffer = createAsyncThunk(
  'offers/evaluateCartOffer',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const state = getState() as RootState
    const { cartId, fingerprint } = resolveCartContext(state)

    // Only evaluate with a real positive cartId
    if (cartId == null || !Number.isFinite(cartId) || cartId <= 0) {
      dispatch(clearOffer())
      return emptyPromotionEvaluation
    }

    const paymentMethod = state.cart.payment_method
    const requestKey = `${cartId}|${paymentMethod ?? ''}|${fingerprint}`
    if (requestKey === state.offers.lastEvalKey) {
      return state.offers.evaluation
    }

    const requestId = state.offers.requestId + 1
    dispatch(beginOfferEvaluation({ requestKey, requestId }))

    try {
      const body: { cartId: number; paymentMethod?: string } = { cartId }
      if (paymentMethod) {
        body.paymentMethod = paymentMethod
      }

      const response = await apiService.axios.post(
        buildEndpoint('/cashier/promotions/evaluate'),
        body,
      )

      const current = getState() as RootState
      if (requestId !== current.offers.requestId) {
        return rejectWithValue('stale')
      }

      return promotionEvaluationFromResponse(response.data)
    } catch (error) {
      const current = getState() as RootState
      if (requestId !== current.offers.requestId) {
        return rejectWithValue('stale')
      }
      if (import.meta.env.DEV) {
        console.warn('Promotion evaluate error:', error)
      }
      return emptyPromotionEvaluation
    }
  },
)

const offersSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    beginOfferEvaluation: (
      state,
      action: PayloadAction<{ requestKey: string; requestId: number }>,
    ) => {
      state.lastEvalKey = action.payload.requestKey
      state.requestId = action.payload.requestId
      state.isLoading = true
      state.evaluation = emptyPromotionEvaluation
    },
    clearOffer: (state) => {
      state.lastEvalKey = null
      state.requestId += 1
      state.isLoading = false
      state.evaluation = emptyPromotionEvaluation
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(evaluateCartOffer.fulfilled, (state, action) => {
        if (action.meta.requestStatus !== 'fulfilled') return
        state.evaluation = action.payload ?? emptyPromotionEvaluation
        state.isLoading = false
      })
      .addCase(evaluateCartOffer.rejected, (state, action) => {
        if (action.payload === 'stale') return
        state.evaluation = emptyPromotionEvaluation
        state.isLoading = false
      })
  },
})

export const { beginOfferEvaluation, clearOffer } = offersSlice.actions

export const selectOfferEvaluation = (state: RootState) => state.offers.evaluation
export const selectIsOfferLoading = (state: RootState) => state.offers.isLoading

export default offersSlice.reducer
